import React from 'react';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircleIcon from '@mui/icons-material/Circle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import useUser from '../../hooks/useUser';
import {formatCurrency} from '../../helpers/currency';
import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import {useGetLocationsQuery} from '../../redux/services/locationApi';
import {getServiceCategories} from '../../helpers/selectOptions';
import CustomBox from '../common/CustomBox';
import ChoiceField from '../common/ChoiceField';
import CustomSelect from '../common/CustomSelect';

const locationChoices = [
  {label: '🏠 In-Home', value: 'HOME'},
  {label: '🚗 Mobile', value: 'MOBILE'},
  {label: '💻 Virtual', value: 'VIRTUAL'},
  {label: '🏋️ Facility', value: 'PARTNER'},
];

const createNewConfig = (session_type_id) => ({
  session_type_id,
  location_types: [],
  facilities: [],
});

const handleCheckChange = ({isSelected, onChange, configs, sessionType}) => (e) => {
  onChange(
    e,
    isSelected
    ? configs.filter(c => c.session_type_id !== sessionType.id)
    : [...configs, createNewConfig(sessionType.id)]
  );
};

const handleLocationTypeChange = ({onChange, config, configs, sessionType}) => e => {
  const newConfig = {
    ...config,
    location_types: e.target.value,
    facilities: e.target.value.includes('PARTNER') 
      ? config.facilities || []
      : [],
  };
  onChange(
    e,
    configs.map(c => c.session_type_id === sessionType.id ? newConfig : c)
  );
};

const handleFacilityChange = ({config, onChange, configs, sessionType}) => (e, value) => {
  const _value = value || e?.target?.value;
  console.log('handleFacilityChange', _value);
  const newConfig = 
    isArray(config?.facility_ids)
    ? {...config, facility_ids: _value.map(v => v?.value || v)}
    : {...config, facility_ids: []};
  const newConfigs = configs.map(
    c => c.session_type_id === sessionType.id ? newConfig : c
  );
  onChange(
    {...e, target: {...e.target, value: newConfigs}},
    newConfigs
  );
};

const SessionConfigItem = ({
  sessionType,
  onSelect,
  configs = [],
  facilities = [],
  onChange,
}) => {
  const {color, label} = getServiceCategories()
    .find(c => c.value === sessionType.service_category);
  const isSelected = configs.some(c => c.session_type_id === sessionType.id);
  const config = configs.find(c => c.session_type_id === sessionType.id);
  if (config?.location_types?.includes('PARTNER')) console.log('CONFIG', config, facilities);
  return (
    <CustomBox>
      <Box style={{padding: '0.5em'}}>
        <Stack>
          <Stack direction='row' spacing={0.75} alignItems='center'>
            <Checkbox 
              defaultValue={isSelected}
              checked={isSelected}
              onChange={handleCheckChange({isSelected, onChange, configs, sessionType})}
            />
            <CircleIcon style={{fontSize: '1rem', color}} />
            <Stack>
              <Stack direction='row' spacing={0.5} alignItems='center' pt={2}>
                <Typography variant='h3'>{label}</Typography>
              </Stack>
              <Stack direction='row' spacing={1}>
                <Typography variant='body1' color='text.muted'>
                  <strong>Eval</strong>: {formatCurrency(sessionType.eval_hourly_rate)}
                </Typography>
                <Typography variant='body1' color='text.muted'>
                  <strong>Follow-up</strong>: {formatCurrency(sessionType.hourly_rate)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      {isSelected && 
        <Box 
          style={{
            padding: '1em', 
            paddingTop: '0em',
            borderTop: '1px solid rgb(240,240,240)',
            backgroundColor: 'rgb(250,250,250)',
            borderRadius: '0 0 8px 8px',
            border: '1px solid rgb(240,240,240)',
          }}
        >
          <Stack>
            <ChoiceField
              multi={true}
              label='Locations'
              value={config?.location_types || []}
              options={locationChoices}
              onChange={handleLocationTypeChange({
                onChange, 
                config, 
                configs, 
                sessionType
              })}
            />
            {
              config?.location_types.includes('PARTNER') && (
                <Box>
                  <CustomSelect
                    multiple={true}
                    label='Facilities'
                    defaultValue={(config?.facility_ids || []).map(facilityId => facilityId?.value || facilityId?.id || facilityId)}
                    fullWidth={true}
                    value={(config?.facility_ids || []).map(facilityId => facilityId?.value || facilityId?.id || facilityId)}
                    options={(facilities || [])
                      .map(f => ({label: f.name, value: f.id}))
                    }
                    onChange={handleFacilityChange({
                      onChange, 
                      config, 
                      configs, 
                      sessionType
                    })}
                  />
                  {facilities.length === 0 && 
                    <Typography variant='body2' color='text.muted'>
                      No facilities found in availability zone
                    </Typography>
                  }
                </Box>
              )
            }
          </Stack>
        </Box>
      }
    </CustomBox>
  );
};

const SessionConfigEditor = props => {
  const sessionTypes = props.sessionTypes;
  const configs = props.value != null ? props.value : (props.defaultValue || []);
  return (
    <Box mt={2}>
      <Stack spacing={1}>
        {props.error && 
          <Alert variant='outlined' severity='error'>
            {props?.helperText || 'Please enter at least one Session Configuration'}
          </Alert>
        }
        {sessionTypes.map((st, i) => (
          <SessionConfigItem
            key={st?.id || i}
            sessionType={st}
            config={configs.find(c => c.session_type_id === st.id)}
            configs={configs}
            facilities={props.facilities}
            onChange={(e, value) => {
              if (props.onChange) props.onChange(e, value);
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

const SessionConfigEditorContainer = props => {
  const user = useUser();
  const {lat, lon, distance} = props?.geolocation || {};
  const sessionTypesQuery = useGetSessionTypesQuery(
    {provider_id: user.provider.id, session_type_version: '2.0'},
    {skip: !user.provider.id}
  );
  const locationsQuery = useGetLocationsQuery(
    {lat, lon, distance: distance === 0 ? 0.1 : distance}, 
    {skip: !lat || !lon || (!distance && distance !== 0)}
  );
  if (sessionTypesQuery.isLoading) return <div/>;
  const sessionTypes = sessionTypesQuery.data || [];
  const facilities = locationsQuery?.data?.data || [];
  return (
    <SessionConfigEditor
      sessionTypes={sessionTypes}
      defaultValue={props?.defaultValue || []}
      value={props.value}
      onChange={props.onChange}
      facilities={facilities}
      error={props.error}
      helperText={props.helperText}
    />
  );
};

SessionConfigEditor.defaultProps = {
  geolocation: {
    lat: null,
    lon: null,
    distance: null,
  },
  onChange: (e, value) => console.log('onChange', value),
};

SessionConfigEditorContainer.propTypes = {
  sessionTypeConfigs: PropTypes.array.isRequired,
  isNewAvailabilityZone: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({
      session_type_id: PropTypes.number.isRequired,
      location_types: PropTypes.arrayOf(PropTypes.string).isRequired,
      facilities: PropTypes.arrayOf(PropTypes.number),
    })
  ),
  geolocation: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
    distance: PropTypes.number,
  }),
  onChange: PropTypes.func,
};

export default SessionConfigEditorContainer;